<!--
	Morph — the sticky scroll-driven hero morph (plan/03 §3, §5). A tall scroll
	container with a sticky canvas behind flowing chapter content (passed as
	children). three.js is code-split and initialised on idle AFTER first paint
	(LCP is not the canvas); the canvas cross-fades in on the first frame.
	Reduced-motion / save-data keep the static poster. The canvas is aria-hidden;
	the chapters are real headings in the DOM, readable with no JS.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import type { Snippet } from 'svelte';
	import { scroll } from '$lib/motion/scroll.svelte';
	import { motionDisabled } from '$lib/motion/reduced';
	import type { MorphScene } from '$lib/three/MorphScene';

	let {
		heightVh = 320,
		class: klass = '',
		/** Poster image under the canvas — what no-JS / reduced-motion / WebGL-fail
		    visitors see, and what paints while three.js loads on idle. */
		poster = '',
		posterAlt = '',
		children
	}: {
		heightVh?: number;
		class?: string;
		poster?: string;
		posterAlt?: string;
		children?: Snippet;
	} = $props();

	let container: HTMLElement;
	let sticky: HTMLElement;
	let canvas: HTMLCanvasElement;
	let rangeId = $state('');
	let ready = $state(false);
	let scene: MorphScene | null = null;

	const progress = $derived(rangeId ? scroll.progress(rangeId) : 0);

	onMount(() => {
		const reg = scroll.register(container);
		rangeId = reg.id;

		let disposed = false;
		let ro: ResizeObserver | undefined;

		async function init() {
			if (disposed || motionDisabled()) return; // reduced-motion keeps the poster
			try {
				const mod = await import('$lib/three/MorphScene');
				if (disposed) return;
				scene = new mod.MorphScene(canvas);
				const size = () => {
					const r = sticky.getBoundingClientRect();
					scene?.resize(r.width, r.height);
				};
				ro = new ResizeObserver(size);
				ro.observe(sticky);
				size();
				await scene.ready;
				if (disposed) return;
				scene.setProgress(progress);
				scene.render();
				ready = true;
			} catch (err) {
				// WebGL unavailable / context lost / asset failure → fail safe to the
				// poster grid (plan/03 §5), never an uncaught error.
				console.warn('[morph] scene init failed — keeping poster:', err);
				scene?.dispose();
				scene = null;
			}
		}

		const idle: (cb: () => void) => void =
			typeof window.requestIdleCallback === 'function'
				? (cb) => window.requestIdleCallback(cb)
				: (cb) => window.setTimeout(cb, 200);
		idle(init);

		return () => {
			disposed = true;
			reg.dispose();
			ro?.disconnect();
			scene?.dispose();
			scene = null;
		};
	});

	// Drive the render from the scroll loop: progress ($state) updates each frame.
	$effect(() => {
		const p = progress;
		if (scene && ready) {
			scene.setProgress(p);
			scene.render();
		}
	});
</script>

<div bind:this={container} class="morph {klass}" style="height: {heightVh}vh">
	<div bind:this={sticky} class="morph__sticky">
		{#if poster}
			<img
				class="morph__poster"
				class:is-hidden={ready}
				src={poster}
				alt={posterAlt}
				loading="lazy"
				decoding="async"
			/>
		{/if}
		<canvas bind:this={canvas} class="morph__canvas" class:is-ready={ready} aria-hidden="true"
		></canvas>
	</div>
	<div class="morph__content">
		{@render children?.()}
	</div>
</div>

<style>
	.morph {
		position: relative;
		width: 100%;
	}
	.morph__sticky {
		position: sticky;
		top: 0;
		height: 100vh;
		width: 100%;
		overflow: hidden;
		/* Rest / no-JS poster: the registration grid reads as intent before the
		   canvas cross-fades in — same faint blue as the hero's scan backdrop,
		   so the two sections share one atmosphere across the seam. */
		background-image:
			linear-gradient(rgba(70, 110, 180, 0.06) 1px, transparent 1px),
			linear-gradient(90deg, rgba(70, 110, 180, 0.06) 1px, transparent 1px);
		background-size: 48px 48px;
		background-position: center;
	}
	.morph__poster {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		/* The poster is a capture of the scene's own end state — cover, so it
		   lands exactly where the canvas picks up (no letterbox seam). */
		object-fit: cover;
		opacity: 1;
		transition: opacity 500ms ease;
	}
	.morph__poster.is-hidden {
		opacity: 0;
	}
	.morph__canvas {
		position: absolute;
		inset: 0;
		height: 100%;
		width: 100%;
		opacity: 0;
		transition: opacity 500ms ease;
	}
	.morph__canvas.is-ready {
		opacity: 1;
	}
	/* Pull the chapters up to overlap the sticky canvas, so they scroll over it. */
	.morph__content {
		position: relative;
		margin-top: -100vh;
		pointer-events: none;
	}
	.morph__content :global(a),
	.morph__content :global(button) {
		pointer-events: auto;
	}
</style>
